import { supabase } from './supabase';

export interface SiteContentRecord<T = any> {
  key?: string;
  content?: T | null;
  draft_content?: T | null;
  published_content?: T | null;
  draft_updated_at?: string | null;
  last_published_at?: string | null;
  updated_at?: string | null;
}

export const SITE_CONTENT_SELECT =
  'key, content, draft_content, published_content, draft_updated_at, last_published_at, updated_at';

export const SITE_CONTENT_PUBLIC_SELECT =
  'key, content, published_content, last_published_at, updated_at';

export async function loadSiteContentForAdmin<T>(key: string) {
  return supabase
    .from('site_content')
    .select(SITE_CONTENT_SELECT)
    .eq('key', key)
    .maybeSingle<SiteContentRecord<T>>();
}

export function resolveSiteContentDraft<T>(row: SiteContentRecord<T> | null | undefined, fallback: T): T {
  return (row?.draft_content ?? row?.content ?? fallback) as T;
}

export function resolveSiteContentPublished<T>(row: SiteContentRecord<T> | null | undefined, fallback: T): T {
  const published = row?.published_content;
  if (published != null) return published as T;

  if (import.meta.env.DEV) {
    return (row?.draft_content ?? row?.content ?? fallback) as T;
  }

  return fallback;
}

export async function saveSiteContentDraft<T>(key: string, draftContent: T) {
  const nowIso = new Date().toISOString();
  const updateResult = await supabase
    .from('site_content')
    .update({
      draft_content: draftContent,
      content: draftContent,
      draft_updated_at: nowIso,
    })
    .eq('key', key)
    .select('key, draft_updated_at')
    .maybeSingle<SiteContentRecord<T>>();

  if (updateResult.error) {
    return { data: null, error: updateResult.error };
  }
  if (updateResult.data) {
    return { data: updateResult.data, error: null };
  }

  return supabase
    .from('site_content')
    .insert({
      key,
      content: draftContent,
      draft_content: draftContent,
      published_content: draftContent,
      draft_updated_at: nowIso,
      last_published_at: nowIso,
    })
    .select('key, draft_updated_at')
    .single<SiteContentRecord<T>>();
}

export async function publishSiteContentDraft(key: string) {
  return supabase.rpc('publish_site_content_key', { p_key: key });
}

export async function revertSiteContentDraftToPublished(key: string) {
  return supabase.rpc('revert_site_content_draft_to_published', { p_key: key });
}
